/**
 * LLM Analyses Component
 * 
 * Central collection point for all LLM-generated information across the app.
 * Can render as:
 * - Dropdown (navbar) - legacy, currently unused
 * - Full page (asPage=true) - shown via profiel dropdown menu
 */

import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { InsightIcon, ConnectionLinkIcon, DocumentIcon, PartnerIcon, TagIcon, AnalyticsIcon } from './icons';
import ExpertStatusCard from './vibes/ExpertStatusCard';
import AIVisibilityImpactCard from './vibes/AIVisibilityImpactCard';

interface LLMAnalysesDropdownProps {
  userId: string;
  onNavigate?: (page: string, subPage?: string) => void;
  /** Render as full page instead of dropdown */
  asPage?: boolean;
}

interface ConnectionFlowAnalysis {
  id: string;
  connectionId: string;
  otherUserId: string;
  otherUserName?: string;
  flowSummary: {
    chatSummary: string;
    callSummary: string;
    videoSummary: string;
    physicalSummary: string;
    location: string;
  };
  createdAt: any;
}

// ============================================================================
// PAGE VIEW - Full page rendering
// ============================================================================

// Info accordion item (same pattern as homepage)
const LLMInfoAccordionItem: React.FC<{ title: string; items: string[] }> = ({ title, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-[#0A2342]">{title}</span>
        <svg
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <ul className="px-4 pb-2.5 space-y-1.5">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
              <span className="mt-1 w-1 h-1 rounded-full bg-[#7A9488] flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const llmInfoSections = [
  {
    title: 'Expert Status',
    items: [
      'Toont je huidige expert niveau op basis van activiteit en gesprekken.',
      'Status Health geeft aan hoe actief je bent — zakt als je inactief wordt.',
      'Bouw je reputatie op door regelmatig in te loggen en gesprekken te voeren.',
      'Tip: Blijf regelmatig inloggen en plan gesprekken om je status te behouden.',
    ],
  },
  {
    title: 'Visibility Impact',
    items: [
      'Score (0-100) die toont hoe goed AI-zoekmachines jouw expertise kunnen begrijpen en aanbevelen.',
      'Gebaseerd op kwaliteit, context en samenwerking — geen likes, geen pay-to-win.',
      'Factoren: Expert Status, Connecties, Netwerkclubs en Samenwerkingen.',
      'Klik op de kaart voor een gedetailleerd overzicht.',
    ],
  },
  {
    title: 'Connectieflow Analyses',
    items: [
      'AI-gegenereerde samenvattingen van je connectieflow fases (chat, call, video, fysiek).',
      'Worden automatisch aangemaakt na het voltooien van connectieflow stappen.',
      'Helpt je om je netwerk interacties terug te lezen en te evalueren.',
    ],
  },
  {
    title: 'Respect Analyses',
    items: [
      'Binnenkort beschikbaar — analyseert wederzijds respect in je connecties.',
      'Gebaseerd op interactiepatronen en feedback van je netwerk.',
    ],
  },
  {
    title: 'Tag & Share Analyses',
    items: [
      'Binnenkort beschikbaar — analyseert hoe je content wordt gedeeld en getagd.',
      'Toont je bereik en invloed binnen de community.',
    ],
  },
];

const LLMAnalysesPage: React.FC<{ userId: string }> = ({ userId }) => {
  const [analyses, setAnalyses] = useState<ConnectionFlowAnalysis[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const infoPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userId) return;

    const loadAnalyses = async () => {
      setIsLoading(true);
      try {
        const analysesRef = collection(db, 'connectionFlowAnalyses');
        const q = query(
          analysesRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );

        const snapshot = await getDocs(q);
        const analysesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ConnectionFlowAnalysis[];

        setAnalyses(analysesData);
      } catch (error) {
        console.error('Error loading LLM analyses:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalyses();
  }, [userId]);

  // Click outside to close info panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (infoPanelRef.current && !infoPanelRef.current.contains(event.target as Node)) {
        setShowInfoPanel(false);
      }
    };
    if (showInfoPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showInfoPanel]);

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <InsightIcon size={24} weight="regular" className="text-gray-700" />
              <span>LLM Expertstatus</span>
            </h1>
            <p className="text-gray-600 mt-1">
              Overzicht van alle AI-gegenereerde analyses en inzichten
            </p>
          </div>
          {/* Unified info button */}
          <div className="relative" ref={infoPanelRef}>
            <button
              onClick={() => setShowInfoPanel(!showInfoPanel)}
              className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center transition-colors mt-1
                ${showInfoPanel
                  ? 'bg-[#7A9488] text-white'
                  : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                }`}
              title="Informatie over alle onderdelen"
            >
              i
            </button>
            {showInfoPanel && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
                  <span className="text-sm font-semibold text-[#0A2342]">Hoe werkt het?</span>
                  <button
                    onClick={() => setShowInfoPanel(false)}
                    className="text-gray-400 hover:text-gray-600 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                  {llmInfoSections.map((section) => (
                    <LLMInfoAccordionItem key={section.title} title={section.title} items={section.items} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Expert Status Card */}
        <div className="mb-6">
          <ExpertStatusCard userId={userId} />
        </div>

        {/* AI Visibility Impact Card */}
        <div className="mb-6">
          <AIVisibilityImpactCard userId={userId} />
        </div>

        {/* Connection Flow Analyses */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <ConnectionLinkIcon size={20} className="text-gray-500" />
              <span>Connectieflow Analyses</span>
            </h2>
          </div>
          <div className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7A9488]"></div>
                <span className="ml-3 text-gray-500">Analyses laden...</span>
              </div>
            ) : analyses.length === 0 ? (
              <div className="text-center py-8">
                <DocumentIcon size={32} className="text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nog geen connectieflow analyses beschikbaar</p>
                <p className="text-gray-400 text-sm mt-1">
                  Analyses worden aangemaakt na het voltooien van connectieflow fases
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {analyses.map((analysis) => (
                  <div
                    key={analysis.id}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-800">
                        Flow met {analysis.otherUserName || 'Gebruiker'}
                      </h3>
                      <span className="text-xs text-gray-400">
                        {analysis.createdAt?.toDate?.()?.toLocaleDateString('nl-NL') || ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {analysis.flowSummary.location || 'Geen locatie'}
                    </p>
                    {analysis.flowSummary.chatSummary && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {analysis.flowSummary.chatSummary}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Future sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 opacity-60">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <PartnerIcon size={18} className="text-gray-400" />
              <span>Respect Analyses</span>
            </h3>
            <p className="text-sm text-gray-400">Binnenkort beschikbaar</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 opacity-60">
            <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-2">
              <TagIcon size={18} className="text-gray-400" />
              <span>Tag & Share Analyses</span>
            </h3>
            <p className="text-sm text-gray-400">Binnenkort beschikbaar</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT - Dropdown or Page
// ============================================================================

const LLMAnalysesDropdown: React.FC<LLMAnalysesDropdownProps> = ({ userId, onNavigate, asPage = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [analyses, setAnalyses] = useState<ConnectionFlowAnalysis[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Load connection flow analyses (only for dropdown mode)
  useEffect(() => {
    if (asPage) return;
    if (!userId || !showDropdown) return;
    
    const loadAnalyses = async () => {
      setIsLoading(true);
      try {
        const analysesRef = collection(db, 'connectionFlowAnalyses');
        const q = query(
          analysesRef,
          where('userId', '==', userId),
          orderBy('createdAt', 'desc')
        );
        
        const snapshot = await getDocs(q);
        const analysesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as ConnectionFlowAnalysis[];
        
        setAnalyses(analysesData);
      } catch (error) {
        console.error('Error loading LLM analyses:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAnalyses();
  }, [userId, showDropdown]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // If rendering as page, use the page component (after all hooks)
  if (asPage) {
    return <LLMAnalysesPage userId={userId} />;
  }
  
  const handleMenuItemClick = (action: string) => {
    setShowDropdown(false);
    if (onNavigate) {
      if (action === 'connectionFlowAnalyses') {
        console.log('Navigate to Connection Flow Analyses');
      } else if (action === 'dashboard') {
        onNavigate('dashboard', 'llm-dashboard');
      }
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative px-4 py-2 text-sm font-medium rounded-lg transition-all text-gray-600 hover:text-gray-900 hover:bg-gray-50 flex items-center gap-1"
      >
        <span className="flex items-center gap-1.5"><InsightIcon size={16} weight="regular" className="text-gray-600" /> LLM Expertstatus</span>
        <span className="text-xs">▼</span>
      </button>
      
      {showDropdown && (
        <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            {/* Connection Flow Analyses */}
            <div className="px-4 py-2 border-b border-gray-200">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                <ConnectionLinkIcon size={12} className="text-gray-400 inline mr-1" /> Connectieflow Analyses
              </div>
              {isLoading ? (
                <div className="text-sm text-gray-500 py-2">Laden...</div>
              ) : analyses.length === 0 ? (
                <div className="text-sm text-gray-500 py-2">Geen analyses beschikbaar</div>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {analyses.slice(0, 5).map((analysis) => (
                    <button
                      key={analysis.id}
                      onClick={() => handleMenuItemClick('connectionFlowAnalyses')}
                      className="w-full text-left px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded"
                    >
                      <div className="font-medium truncate">
                        Flow met {analysis.otherUserName || 'Gebruiker'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {analysis.flowSummary.location || 'Geen locatie'}
                      </div>
                    </button>
                  ))}
                  {analyses.length > 5 && (
                    <div className="text-xs text-gray-500 px-2 py-1">
                      +{analyses.length - 5} meer...
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Respect Analyses (Future) */}
            <button
              onClick={() => handleMenuItemClick('respectAnalyses')}
              disabled
              className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <PartnerIcon size={14} className="text-gray-400" />
              <span>Respect Analyses</span>
              <span className="text-xs text-gray-400 ml-auto">(toekomst)</span>
            </button>
            
            {/* Tag & Share Analyses (Future) */}
            <button
              onClick={() => handleMenuItemClick('tagShareAnalyses')}
              disabled
              className="w-full text-left px-4 py-2 text-sm text-gray-400 hover:bg-gray-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <TagIcon size={14} className="text-gray-400" />
              <span>Tag & Share Analyses</span>
              <span className="text-xs text-gray-400 ml-auto">(toekomst)</span>
            </button>
            
            {/* Dashboard */}
            <button
              onClick={() => handleMenuItemClick('dashboard')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-200"
            >
              <AnalyticsIcon size={14} className="text-gray-500" />
              <span>Overzicht Dashboard</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMAnalysesDropdown;
